import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSchuhversorgung } from "../../context/SchuhversorgungContext";
import { useKategorisierung } from "../../context/KategorisierungContext";
import { useAllgemeineDaten } from "../../context/AllgemeineDatenContext";
import styles from "./schuhversorgung.module.css";

export default function Schuhversorgung() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [])
  
  const { data, toggle } = useSchuhversorgung();
  const { data: kategorisierung } = useKategorisierung();

    const { data: allgemeineData } = useAllgemeineDaten();
    const fussAuswahl = allgemeineData?.fussAuswahl;
  
    const showLeftFoot = fussAuswahl !== "rechts";
    const showRightFoot = fussAuswahl !== "links";
    const showBothFeet = showLeftFoot && showRightFoot;


const leftRisiko  = kategorisierung?.linksRisikotyp
  ? String(kategorisierung.linksRisikotyp).replace('Typ ', '').trim()
  : '';

const rightRisiko = kategorisierung?.rechtsRisikotyp
  ? String(kategorisierung.rechtsRisikotyp).replace('Typ ', '').trim()
  : '';

const getFootCellClass = (side, rowType) => {
  const value = side === 'left' ? leftRisiko : rightRisiko;
  
  // Only these exact strings are considered valid for highlighting
  const validTypes = [
    '0', 'I', 'IIa', 'IIb', 'III', 'IV', 'V', 'VI',
    'VIIa', 'VIIb', 'VIIc', 'VIId'
  ];

  // Highlight only if value exactly matches the row AND is in valid list
  if (value && validTypes.includes(value) && value === rowType) {
    return `${styles.cell} ${styles.highlighted}`;
  }

  // Default → no highlight
  return styles.cell;
};

const [errors, setErrors] = useState({});

// [DISABLED] Auto-uncheck selections below the calculated risk group.
// Kept here in case the restriction needs to be restored in the future.
// useEffect(() => {
//   const clearInvalidSelections = (side) => {
//     const risiko = side === "left" ? leftRisiko : rightRisiko;
//     if (!risiko || risiko === "Kein Typ") return;
//     const currentRank = getRiskRank(risiko);
//     if (currentRank === -1) return;
//     const rowMapping = {
//       "0": ["konfekt"],
//       "I": ["einlage", "zurichtung", "massschuh"],
//       "IIa": ["weichsohle", "weichbettung"],
//       "IIb": ["daf_iib"],
//       "III": ["daf_iii", "ortho_zurichtung"],
//       "IV": ["daf_iv"],
//       "V": ["usg", "osg", "ruhig_wade"],
//       "VI": ["amputation", "prothese"],
//       "VIIa": ["verband"],
//       "VIIb": ["nicht_abnehmbar", "abnehmbar"],
//       "VIIc": ["konfektioniert", "individuell", "interimsschuh",
//                "nicht_abnehmbar_or_these", "abnehmbar_or_these",
//                "konfektioniert_nicht_abnehmbar", "konfektioniert_abnehmbar",
//                "individuell_nicht_abnehmbar", "individuell_abnehmbar"],
//       "VIId": ["nicht_abnehmbar_ferse", "abnehmbar_ferse"],
//     };
//     Object.entries(rowMapping).forEach(([rowType, keys]) => {
//       const rowRank = getRiskRank(rowType);
//       const isBlocked = currentRank === 0 ? rowRank >= 4 : rowRank < currentRank;
//       if (isBlocked) {
//         keys.forEach((key) => { if (data?.[side]?.[key] === "ja") toggle(side, key); });
//       }
//     });
//   };
//   clearInvalidSelections("left");
//   clearInvalidSelections("right");
// }, [leftRisiko, rightRisiko, data, toggle]);

// Helper: Get the "rank" number for a risk type (higher = more severe)
// Used to decide what to block
const getRiskRank = (type) => {
  const rankMap = {
    '0': 0,
    'I': 1,
    'IIa': 2,
    'IIb': 3,
    'III': 4,
    'IV': 5,
    'V': 6,
    'VI': 7,
    'VIIa': 8,
    'VIIb': 9,
    'VIIc': 10,
    'VIId': 11
  };
  return rankMap[type] ?? -1;
};

// Is this row blocked for the given side?
// [DISABLED] Row blocking logic — kept for reference if restriction needs to be restored.
// const isRowBlockedForSide = (side, rowType) => {
//   const value = side === 'left' ? leftRisiko : rightRisiko;
//   if (!value || value === 'Kein Typ') return false;
//   const currentRank = getRiskRank(value);
//   const rowRank     = getRiskRank(rowType);
//   if (currentRank === -1) return false;
//   if (currentRank === 0) return rowRank >= 4; // Typ 0 blocks III and above
//   return rowRank < currentRank; // block everything lower than current type
// };
const isRowBlockedForSide = () => false; // all rows freely selectable


const isChecked = (side, key) => data?.[side]?.[key] === "ja";


// VIIc helpers for combined state
const VIIc_KEYS = [
  "konfektioniert_nicht_abnehmbar",
  "konfektioniert_abnehmbar", 
  "individuell_nicht_abnehmbar",
  "individuell_abnehmbar",
  "interimsschuh"
];

const getVIIcSelection = (side) => {
  const sideData = data?.[side] || {};
  
  // Check new combined keys first
  const newKey = VIIc_KEYS.find(k => sideData[k] === "ja");
  if (newKey) return newKey;
  
  // Legacy compatibility
  if (sideData.konfektioniert === "ja") {
    return sideData.nicht_abnehmbar_or_these === "ja" 
      ? "konfektioniert_nicht_abnehmbar" 
      : sideData.abnehmbar_or_these === "ja" 
      ? "konfektioniert_abnehmbar" 
      : "";
  }
  if (sideData.individuell === "ja") {
    return sideData.nicht_abnehmbar_or_these === "ja" 
      ? "individuell_nicht_abnehmbar" 
      : sideData.abnehmbar_or_these === "ja" 
      ? "individuell_abnehmbar" 
      : "";
  }
  if (sideData.interimsschuh === "ja") return "interimsschuh";
  
  return "";
};

const setVIIcSelection = (side, nextValue) => {
  // Clear ALL possible VIIc keys first
const allVIIcKeys = [
  "konfektioniert", "individuell", "interimsschuh",
  "nicht_abnehmbar_or_these", "abnehmbar_or_these",
  ...VIIc_KEYS
];
  
  allVIIcKeys.forEach(key => {
    if (data?.[side]?.[key] === "ja") toggle(side, key);
  });
  
  if (nextValue) {
    toggle(side, nextValue);
  }
};


const handleChange = (side, key) => () => {
  toggle(side, key);
  // Clear error for this side as soon as user interacts
  setErrors((prev) => {
    const newErr = { ...prev };
    delete newErr[`${side}Foot`];
    return newErr;
  });
};

const validateForm = () => {
  const newErrors = {};

  // Only validate LEFT foot when shown
  if (showLeftFoot) {
    const leftKeys = [
      "konfekt",
      "einlage", "zurichtung", "massschuh",
      "weichsohle", "weichbettung",
      "daf_iib",
      "daf_iii", "ortho_zurichtung",
      "daf_iv",
      "usg", "osg", "ruhig_wade",
      "amputation", "prothese",
      "verband",
      "nicht_abnehmbar", "abnehmbar",
      "konfektioniert_nicht_abnehmbar", "konfektioniert_abnehmbar",
      "individuell_nicht_abnehmbar", "individuell_abnehmbar", "interimsschuh",
      "nicht_abnehmbar_ferse", "abnehmbar_ferse",
    ];
    const leftSelected = leftKeys.some(key => data?.left?.[key] === "ja");
    if (!leftSelected) {
      newErrors.leftFoot = "Bitte wählen Sie eine Option";
    }
  }

  // Only validate RIGHT foot when shown  
  if (showRightFoot) {
    const rightKeys = [
      "konfekt",
      "einlage", "zurichtung", "massschuh",
      "weichsohle", "weichbettung",
      "daf_iib",
      "daf_iii", "ortho_zurichtung",
      "daf_iv",
      "usg", "osg", "ruhig_wade",
      "amputation", "prothese",
      "verband",
      "nicht_abnehmbar", "abnehmbar",
      "konfektioniert_nicht_abnehmbar", "konfektioniert_abnehmbar",
      "individuell_nicht_abnehmbar", "individuell_abnehmbar", "interimsschuh",
      "nicht_abnehmbar_ferse", "abnehmbar_ferse",
    ];
    const rightSelected = rightKeys.some(key => data?.right?.[key] === "ja");
    if (!rightSelected) {
      newErrors.rightFoot = "Bitte wählen Sie eine Option";
    }
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};


// VIIc renderer with sub-options
const renderVIIcOptions = (side) => {
  const selected = getVIIcSelection(side);
  const primary = selected.includes("_") ? selected.split("_")[0] : selected;
  const sub = selected.includes("nicht_abnehmbar") ? "nicht_abnehmbar" : 
              selected.includes("abnehmbar") ? "abnehmbar" : "";
  
  return (
    <>
      {/* 1. konfektioniert + suboptions*/}
      <div className={styles["vii-primary-group"]}>
        <div className={styles["measure-check"]}>
          <label>
            <input
              type="checkbox"
              checked={primary === "konfektioniert"}
              onChange={() => setVIIcSelection(side, primary === "konfektioniert" ? "" : "konfektioniert_nicht_abnehmbar")}
              disabled={isRowBlockedForSide(side, "VIIc")}
            />
            konfektioniert
          </label>
        </div>
        {primary === "konfektioniert" && (
          <div className={styles["vii-sub-options"]}>
            <div className={styles["measure-check"]}>
              <label>
                <input
                  type="checkbox"
                  checked={sub === "nicht_abnehmbar"}
                  onChange={() => setVIIcSelection(side, `konfektioniert_nicht_abnehmbar`)}
                  disabled={isRowBlockedForSide(side, "VIIc")}
                />
                nicht abnehmbar
              </label>
            </div>
            <div className={styles["measure-check"]}>
              <label>
                <input
                  type="checkbox"
                  checked={sub === "abnehmbar"}
                  onChange={() => setVIIcSelection(side, `konfektioniert_abnehmbar`)}
                  disabled={isRowBlockedForSide(side, "VIIc")}
                />
                abnehmbar
              </label>
            </div>
          </div>
        )}
      </div>

      {/* 2. individuell + suboptions*/}
      <div className={styles["vii-primary-group"]}>
        <div className={styles["measure-check"]}>
          <label>
            <input
              type="checkbox"
              checked={primary === "individuell"}
              onChange={() => setVIIcSelection(side, primary === "individuell" ? "" : "individuell_nicht_abnehmbar")}
              disabled={isRowBlockedForSide(side, "VIIc")}
            />
            individuell
          </label>
        </div>
        {primary === "individuell" && (
          <div className={styles["vii-sub-options"]}>
            <div className={styles["measure-check"]}>
              <label>
                <input
                  type="checkbox"
                  checked={sub === "nicht_abnehmbar"}
                  onChange={() => setVIIcSelection(side, `individuell_nicht_abnehmbar`)}
                  disabled={isRowBlockedForSide(side, "VIIc")}
                />
                nicht abnehmbar
              </label>
            </div>
            <div className={styles["measure-check"]}>
              <label>
                <input
                  type="checkbox"
                  checked={sub === "abnehmbar"}
                  onChange={() => setVIIcSelection(side, `individuell_abnehmbar`)}
                  disabled={isRowBlockedForSide(side, "VIIc")}
                />
                abnehmbar
              </label>
            </div>
          </div>
        )}
      </div>

      {/* 3. Interimsschuh*/}
      <div className={styles["measure-check"]}>
        <label>
          <input
            type="checkbox"
            checked={selected === "interimsschuh"}
            onChange={() => setVIIcSelection(side, selected === "interimsschuh" ? "" : "interimsschuh")}
            disabled={isRowBlockedForSide(side, "VIIc")}
          />
          Interimsschuh
        </label>
      </div>
    </>
  );
};


  return (
    <div className={styles["schuhversorgung-container"]} lang="de">
      <h1>Schuhversorgung (technische Auslegung) gemäß Risikogruppe</h1>

      <table className={styles["schuhversorgung-table"]}>
      <thead>
        <tr>
          <th className={styles["col-risiko"]}>Risikotyp</th>
          <th className={styles["col-beschreibung"]}>Beschreibung</th>
          {showLeftFoot && <th className={styles["col-left"]}>Linker Fuß</th>}
          {showRightFoot && <th className={styles["col-right"]}>Rechter Fuß</th>}
        </tr>
      </thead>

        <tbody>
          {/* Row 0 */}
          <tr>
            <td className={styles.risiko}>0</td>
            <td className={styles.beschreibung}>Fußgerechte Konfektionsschuhe</td>
            {showLeftFoot && (
              <td className={`${getFootCellClass('left', '0')} ${isRowBlockedForSide('left', '0') ? styles.blocked : ''}`}>
                <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                  Linker Fuß
                </div>
                <div className={styles["measure-check"]}>
                  <label>
                    <input
                      type="checkbox"
                      checked={isChecked("left", "konfekt")}
                      onChange={handleChange("left", "konfekt")}
                      disabled={isRowBlockedForSide('left', '0')}
                    /> Fußgerechte Konfektionsschuhe
                  </label>
                </div>
              </td>
            )}

            {showRightFoot && (
            <td className={`${getFootCellClass('right', '0')} ${isRowBlockedForSide('right', '0') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Rechter Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "konfekt")}
                    onChange={handleChange("right", "konfekt")}
                    disabled={isRowBlockedForSide('right', '0')}
                  /> Fußgerechte Konfektionsschuhe
                </label>
              </div>
            </td>   )}
          </tr>

          {/* Row I */}
          <tr>
            <td className={styles.risiko}>I</td>
            <td className={styles.beschreibung}>Orthopädieschuhtechnische Versorgung</td>
            {showLeftFoot && (
            <td className={`${getFootCellClass('left', 'I')} ${isRowBlockedForSide('left', 'I') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Linker Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "einlage")}
                    onChange={handleChange("left", "einlage")}
                    disabled={isRowBlockedForSide('left', 'I')}
                  /> mit orthopädischer Einlage
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "zurichtung")}
                    onChange={handleChange("left", "zurichtung")}
                    disabled={isRowBlockedForSide('left', 'I')}
                  /> mit Schuhzurichtung
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "massschuh")}
                    onChange={handleChange("left", "massschuh")}
                    disabled={isRowBlockedForSide('left', 'I')}
                  /> mit Maßschuh
                </label>
              </div>
            </td> )}

            {showRightFoot && (
            <td className={`${getFootCellClass('right', 'I')} ${isRowBlockedForSide('right', 'I') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Rechter Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "einlage")}
                    onChange={handleChange("right", "einlage")}
                    disabled={isRowBlockedForSide('right', 'I')}
                  /> mit orthopädischer Einlage
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "zurichtung")}
                    onChange={handleChange("right", "zurichtung")}
                    disabled={isRowBlockedForSide('right', 'I')}
                  /> mit Schuhzurichtung
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "massschuh")}
                    onChange={handleChange("right", "massschuh")}
                    disabled={isRowBlockedForSide('right', 'I')}
                  /> mit Maßschuh
                </label>
              </div>
            </td> )}
          </tr>

          {/* Row II a */}
          <tr>
            <td className={styles.risiko}>IIa</td>
            <td className={styles.beschreibung}>Für DFS geeignete Schuhe</td>

            {showLeftFoot && (
            <td className={`${getFootCellClass('left', 'IIa')} ${isRowBlockedForSide('left', 'IIa') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Linker Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "weichsohle")}
                    onChange={handleChange("left", "weichsohle")}
                    disabled={isRowBlockedForSide('left', 'IIa')}
                  /> mit herausnehmbarer Weichpolstersohle
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "weichbettung")}
                    onChange={handleChange("left", "weichbettung")}
                    disabled={isRowBlockedForSide('left', 'IIa')}
                  /> mit Weichpolsterbettungseinlage (elastisch, druckumverteilend, nach Maß)
                </label>
              </div>
            </td> )}

            {showRightFoot && (
            <td className={`${getFootCellClass('right', 'IIa')} ${isRowBlockedForSide('right', 'IIa') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Rechter Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "weichsohle")}
                    onChange={handleChange("right", "weichsohle")}
                    disabled={isRowBlockedForSide('right', 'IIa')}
                  /> mit herausnehmbarer Weichpolstersohle
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "weichbettung")}
                    onChange={handleChange("right", "weichbettung")}
                    disabled={isRowBlockedForSide('right', 'IIa')}
                  /> mit Weichpolsterbettungseinlage (elastisch, druckumverteilend, nach Maß)
                </label>
              </div>
            </td> )}
          </tr>

          {/* Row IIb */}
          <tr>
            <td className={styles.risiko}>IIb</td>
            <td className={styles.beschreibung}>Spezialschuhe bei DFS mit DAF</td>

            {showLeftFoot && (
            <td className={`${getFootCellClass('left', 'IIb')} ${isRowBlockedForSide('left', 'IIb') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Linker Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "daf_iib")}
                    onChange={handleChange("left", "daf_iib")}
                    disabled={isRowBlockedForSide('left', 'IIb')}
                  /> mit diabetesadaptierter Fußbettung (DAF)
                </label>
              </div>
            </td> )}

            {showRightFoot && (
            <td className={`${getFootCellClass('right', 'IIb')} ${isRowBlockedForSide('right', 'IIb') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Rechter Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "daf_iib")}
                    onChange={handleChange("right", "daf_iib")}
                    disabled={isRowBlockedForSide('right', 'IIb')}
                  /> mit diabetesadaptierter Fußbettung (DAF)
                </label>
              </div>
            </td> )}
          </tr>

          {/* Row III */}
          <tr>
            <td className={styles.risiko}>III</td>
            <td className={styles.beschreibung}>Spezialschuhe bei DFS mit DAF</td>

            {showLeftFoot && (
            <td className={`${getFootCellClass('left', 'III')} ${isRowBlockedForSide('left', 'III') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Linker Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "daf_iii")}
                    onChange={handleChange("left", "daf_iii")}
                    disabled={isRowBlockedForSide('left', 'III')}
                  /> mit diabetesadaptierter Fußbettung (DAF)
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "ortho_zurichtung")}
                    onChange={handleChange("left", "ortho_zurichtung")}
                    disabled={isRowBlockedForSide('left', 'III')}
                  /> mit orthopädischer Schuhzurichtung
                </label>
              </div>
            </td> )}

            {showRightFoot && (
            <td className={`${getFootCellClass('right', 'III')} ${isRowBlockedForSide('right', 'III') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Rechter Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "daf_iii")}
                    onChange={handleChange("right", "daf_iii")}
                    disabled={isRowBlockedForSide('right', 'III')}
                  /> mit diabetesadaptierter Fußbettung (DAF)
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "ortho_zurichtung")}
                    onChange={handleChange("right", "ortho_zurichtung")}
                    disabled={isRowBlockedForSide('right', 'III')}
                  /> mit orthopädischer Schuhzurichtung
                </label>
              </div>
            </td> )}
          </tr>

          {/* Row IV */}
          <tr>
            <td className={styles.risiko}>IV</td>
            <td className={styles.beschreibung}>Orthopädische Maßschuhe mit DAF</td>

            {showLeftFoot && (
            <td className={`${getFootCellClass('left', 'IV')} ${isRowBlockedForSide('left', 'IV') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Linker Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "daf_iv")}
                    onChange={handleChange("left", "daf_iv")}
                    disabled={isRowBlockedForSide('left', 'IV')}
                  /> mit diabetesadaptierter Fußbettung (DAF)
                </label>
              </div>
            </td> )}

            {showRightFoot && (
            <td className={`${getFootCellClass('right', 'IV')} ${isRowBlockedForSide('right', 'IV') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Rechter Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "daf_iv")}
                    onChange={handleChange("right", "daf_iv")}
                    disabled={isRowBlockedForSide('right', 'IV')}
                  /> mit diabetesadaptierter Fußbettung (DAF)
                </label>
              </div>
            </td> )}
          </tr>

          {/* Row V */}
          <tr>
            <td className={styles.risiko}>V</td>
            <td className={styles.beschreibung}>Knöchelübergreifende orthopädische Maßschuhe mit DAF</td>

            {showLeftFoot && (
            <td className={`${getFootCellClass('left', 'V')} ${isRowBlockedForSide('left', 'V') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Linker Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "usg")}
                    onChange={handleChange("left", "usg")}
                    disabled={isRowBlockedForSide('left', 'V')}
                  /> USG-stabilisierende Maßnahmen
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "osg")}
                    onChange={handleChange("left", "osg")}
                    disabled={isRowBlockedForSide('left', 'V')}
                  /> OSG-stabilisierende Maßnahmen
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "ruhig_wade")}
                    onChange={handleChange("left", "ruhig_wade")}
                    disabled={isRowBlockedForSide('left', 'V')}
                  /> Komplette Ruhigstellung mit Innenschuh über Wadenbauch
                </label>
              </div>
            </td>  )}

            {showRightFoot && (
            <td className={`${getFootCellClass('right', 'V')} ${isRowBlockedForSide('right', 'V') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Rechter Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "usg")}
                    onChange={handleChange("right", "usg")}
                    disabled={isRowBlockedForSide('right', 'V')}
                  /> USG-stabilisierende Maßnahmen
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "osg")}
                    onChange={handleChange("right", "osg")}
                    disabled={isRowBlockedForSide('right', 'V')}
                  /> OSG-stabilisierende Maßnahmen
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "ruhig_wade")}
                    onChange={handleChange("right", "ruhig_wade")}
                    disabled={isRowBlockedForSide('right', 'V')}
                  /> Komplette Ruhigstellung mit Innenschuh über Wadenbauch
                </label>
              </div>
            </td> )}
          </tr>

          {/* Row VI */}
          <tr>
            <td className={styles.risiko}>VI</td>
            <td className={styles.beschreibung}>(Knöchelübergreifende) orthopädische Maßschuhe mit DAF</td>
            {showLeftFoot && (
            <td className={`${getFootCellClass('left', 'VI')} ${isRowBlockedForSide('left', 'VI') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Linker Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "amputation")}
                    onChange={handleChange("left", "amputation")}
                    disabled={isRowBlockedForSide('left', 'VI')}
                  /> Knöchelübergreifende Amputationsversorgung
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "prothese")}
                    onChange={handleChange("left", "prothese")}
                    disabled={isRowBlockedForSide('left', 'VI')}
                  /> Prothese, ggf. bis zum Knie
                </label>
              </div>
            </td> )}

            {showRightFoot && (
            <td className={`${getFootCellClass('right', 'VI')} ${isRowBlockedForSide('right', 'VI') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Rechter Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "amputation")}
                    onChange={handleChange("right", "amputation")}
                    disabled={isRowBlockedForSide('right', 'VI')}
                  /> Knöchelübergreifende Amputationsversorgung
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "prothese")}
                    onChange={handleChange("right", "prothese")}
                    disabled={isRowBlockedForSide('right', 'VI')}
                  /> Prothese, ggf. bis zum Knie
                </label>
              </div>
            </td> )}
          </tr>

          {/* Row VII a */}
          <tr>
            <td className={styles.risiko}>VIIa</td>
            <td className={styles.beschreibung}>Verbandsschuhe</td>

            {showLeftFoot && (
            <td className={`${getFootCellClass('left', 'VIIa')} ${isRowBlockedForSide('left', 'VIIa') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Linker Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "verband")}
                    onChange={handleChange("left", "verband")}
                    disabled={isRowBlockedForSide('left', 'VIIa')}
                  /> Verbandsschuhe
                </label>
              </div>
            </td> )}

            {showRightFoot && (
            <td className={`${getFootCellClass('right', 'VIIa')} ${isRowBlockedForSide('right', 'VIIa') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Rechter Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "verband")}
                    onChange={handleChange("right", "verband")}
                    disabled={isRowBlockedForSide('right', 'VIIa')}
                  /> Verbandsschuhe
                </label>
              </div>
            </td> )}
          </tr>

          {/* Row VIIb */}
          <tr>
            <td className={styles.risiko}>VIIb</td>
            <td className={styles.beschreibung}>Individuelle kniehohe Orthese mit DAF</td>
            {showLeftFoot && (
            <td className={`${getFootCellClass('left', 'VIIb')} ${isRowBlockedForSide('left', 'VIIb') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Linker Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "nicht_abnehmbar")}
                    onChange={handleChange("left", "nicht_abnehmbar")}
                    disabled={isRowBlockedForSide('left', 'VIIb')}
                  /> nicht abnehmbar
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "abnehmbar")}
                    onChange={handleChange("left", "abnehmbar")}
                    disabled={isRowBlockedForSide('left', 'VIIb')}
                  /> abnehmbar
                </label>
              </div>
            </td> )}

            {showRightFoot && (
            <td className={`${getFootCellClass('right', 'VIIb')} ${isRowBlockedForSide('right', 'VIIb') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Rechter Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "nicht_abnehmbar")}
                    onChange={handleChange("right", "nicht_abnehmbar")}
                    disabled={isRowBlockedForSide('right', 'VIIb')}
                  /> nicht abnehmbar
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "abnehmbar")}
                    onChange={handleChange("right", "abnehmbar")}
                    disabled={isRowBlockedForSide('right', 'VIIb')}
                  /> abnehmbar
                </label>
              </div>
            </td> )}
          </tr>

          {/* Row VIIc */}
          <tr>
            <td className={styles.risiko}>VIIc</td>
            <td className={styles.beschreibung}>Kniehohe Orthese</td>
            {showLeftFoot && (
            <td className={`${getFootCellClass('left', 'VIIc')} ${isRowBlockedForSide('left', 'VIIc') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Linker Fuß
              </div>

              {renderVIIcOptions("left")}
            </td>)}

            {showRightFoot && (
            <td className={`${getFootCellClass('right', 'VIIc')} ${isRowBlockedForSide('right', 'VIIc') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Rechter Fuß
              </div>

              {renderVIIcOptions("right")}
            </td> )}
          </tr>

          {/* Row VIId */}
          <tr>
            <td className={styles.risiko}>VIId</td>
            <td className={styles.beschreibung}>Kniehohe individuelle Fersenentlastungsorthese</td>
            {showLeftFoot && (
            <td className={`${getFootCellClass('left', 'VIId')} ${isRowBlockedForSide('left', 'VIId') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Linker Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "nicht_abnehmbar_ferse")}
                    onChange={handleChange("left", "nicht_abnehmbar_ferse")}
                    disabled={isRowBlockedForSide('left', 'VIId')}
                  /> nicht abnehmbar
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("left", "abnehmbar_ferse")}
                    onChange={handleChange("left", "abnehmbar_ferse")}
                    disabled={isRowBlockedForSide('left', 'VIId')}
                  /> abnehmbar
                </label>
              </div>
            </td> )}

            {showRightFoot && (
            <td className={`${getFootCellClass('right', 'VIId')} ${isRowBlockedForSide('right', 'VIId') ? styles.blocked : ''}`}>
              <div className={`${styles["mobile-col-title"]} ${styles["mobile-only"]}`}>
                Rechter Fuß
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "nicht_abnehmbar_ferse")}
                    onChange={handleChange("right", "nicht_abnehmbar_ferse")}
                    disabled={isRowBlockedForSide('right', 'VIId')}
                  /> nicht abnehmbar
                </label>
              </div>
              <div className={styles["measure-check"]}>
                <label>
                  <input
                    type="checkbox"
                    checked={isChecked("right", "abnehmbar_ferse")}
                    onChange={handleChange("right", "abnehmbar_ferse")}
                    disabled={isRowBlockedForSide('right', 'VIId')}
                  /> abnehmbar
                </label>
              </div>
            </td> )}
          </tr>
        </tbody>
      </table>

      <div className={styles["validation-errors"]}>
        {showLeftFoot && errors.leftFoot && (
          <p className={styles["error-text"]}>
            Linker Fuß: {errors.leftFoot}
          </p>
        )}
        {showRightFoot && errors.rightFoot && (
          <p className={styles["error-text"]}>
            Rechter Fuß: {errors.rightFoot}
          </p>
        )}
      </div>

      <div className={`${styles.row} ${styles.actions}`}>
        <button
          type="button"
          className={styles["btn-primary"]}
          onClick={() => navigate("/kategorisierung")}
        >
          Zurück
        </button>
        <button
          type="button"
          className={styles["btn-primary"]}
          onClick={() => {
            if (validateForm()) {
              navigate("/abschluss");
            }
          }}
        >
          Weiter
        </button>
      </div>
    </div>
  );
}